import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import type { RoutingPerson } from '../../api/notificationRouting';

export interface MemberChecklistProps {
  people: RoutingPerson[];
  value: string[];
  onChange: (ids: string[]) => void;
  /** Labels the search box. */
  inputId?: string;
}

/**
 * Who is in a group, as a list to tick — the office, then the event's clients.
 * A dropdown inside a dialog is a list in a list; the people are few enough to
 * show them all, with a search box once there are many.
 */
export function MemberChecklist({ people, value, onChange, inputId }: MemberChecklistProps) {
  const [term, setTerm] = useState('');
  const chosen = useMemo(() => new Set(value), [value]);
  const shown = useMemo(() => {
    const q = term.trim().toLowerCase();
    return q ? people.filter((p) => `${p.name} ${p.detail}`.toLowerCase().includes(q)) : people;
  }, [people, term]);
  const sections = [
    { title: 'Office', list: shown.filter((p) => !p.isClient) },
    { title: 'This event’s clients', list: shown.filter((p) => p.isClient) },
  ].filter((section) => section.list.length > 0);

  const toggle = (id: string) =>
    onChange(chosen.has(id) ? value.filter((v) => v !== id) : [...value, id]);

  return (
    <div className="rounded-lg border border-gray-300">
      <div className="flex items-center gap-2 border-b border-gray-200 px-3">
        <Search size={16} className="text-gray-400" aria-hidden="true" />
        <input
          id={inputId}
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search people"
          className="min-h-11 w-full border-0 bg-transparent text-base outline-none focus:ring-0 sm:min-h-9 sm:text-sm"
        />
        <span className="shrink-0 text-xs text-gray-500">{value.length} chosen</span>
      </div>
      <div className="max-h-64 overflow-y-auto">
        {sections.length === 0 && (
          <p className="px-3 py-4 text-sm text-gray-500">
            {people.length === 0 ? 'Nobody to add yet.' : 'Nobody matches that.'}
          </p>
        )}
        {sections.map((section) => (
          <fieldset key={section.title}>
            <legend className="w-full bg-gray-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {section.title}
            </legend>
            {section.list.map((person) => (
              <label
                key={person.id}
                className="flex min-h-11 cursor-pointer items-center gap-3 px-3 py-1.5 hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={chosen.has(person.id)}
                  onChange={() => toggle(person.id)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-gray-900">{person.name}</span>
                  {person.detail && (
                    <span className="block truncate text-xs text-gray-500">{person.detail}</span>
                  )}
                </span>
              </label>
            ))}
          </fieldset>
        ))}
      </div>
    </div>
  );
}
